import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'

type MaskName = 'blob' | 'corner' | 'band' | 'edge' | 'edge-r' | 'a' | 'b' | 'c' | 'frame'

interface AsciiFieldProps {
    className: string
    seed: number
    mask: MaskName
    cell: number
    power: number
    gain: number
}

const CHARS = ' ·-+x'
const FRAME_MS = 100
const STEP_TIME = 0.09
const STEP_X = 0.04
const STEP_Y = 0.022

const hash = (x: number, y: number, seed: number): number => {
    let n = (x * 374761393 + y * 668265263 + seed * 2246822519) >>> 0

    n = (n ^ (n >> 13)) >>> 0
    n = (n * 1274126177) >>> 0

    return ((n ^ (n >> 16)) >>> 0) / 4294967295
}

const smooth = (t: number): number => t * t * (3 - 2 * t)

const noise = (x: number, y: number, seed: number): number => {
    const x0 = Math.floor(x)
    const y0 = Math.floor(y)
    const fx = x - x0
    const fy = y - y0
    const a = hash(x0, y0, seed)
    const b = hash(x0 + 1, y0, seed)
    const c = hash(x0, y0 + 1, seed)
    const d = hash(x0 + 1, y0 + 1, seed)
    const sx = smooth(fx)
    const sy = smooth(fy)

    return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy
}

const toChar = (value: number): string => {
    const index = Math.min(CHARS.length - 1, Math.max(0, Math.floor(Math.pow(value, 0.85) * CHARS.length * 1.3)))

    return CHARS[index]
}

const MASKS: Record<MaskName, (x: number, y: number) => number> = {
    blob: (x, y) => {
        const d = Math.hypot(x - 0.5, y - 0.5) * 2

        return Math.max(0, 1 - d * d)
    },
    corner: (x, y) => Math.max(0, 1 - (x + y)),
    band: (_x, y) => Math.max(0, 1 - Math.abs(y - 0.5) * 3),
    edge: (x) => Math.max(0, 1 - x * 1.6),
    'edge-r': (x) => Math.max(0, 1 - (1 - x) * 1.6),
    a: (x, y) => smooth(Math.max(0, 1 - Math.hypot((x - 0.28) * 0.95, (y - 0.72) * 1.1) / 1.25)),
    b: (x) => smooth(Math.max(0, 1 - x / 0.58)),
    c: (x, y) => smooth(Math.max(0, 1 - Math.hypot((x - 0.5) * 1.1, (y - 0.35) * 1.35) / 0.85)),
    frame: (x, y) => smooth(Math.min(1, Math.max(0, (Math.hypot((x - 0.5) * 1.8, (y - 0.5) * 2) - 0.42) / 0.75)))
}

const measureCell = (size: number, fontFamily: string): { w: number, h: number } => {
    const probe = document.createElement('span')

    probe.style.cssText = `position:absolute; visibility:hidden; white-space:pre;`
    probe.style.font = `${size}px/${size}px ${fontFamily}`
    probe.textContent = 'x'.repeat(100)
    document.body.appendChild(probe)

    const width = probe.getBoundingClientRect().width / 100

    probe.remove()

    return { w: width || size * 0.6, h: size }
}

// Animated ASCII noise field, drawn into a <pre> exactly like the original site's AsciiField script.
export const AsciiField: FC<AsciiFieldProps> = ({
    className,
    seed,
    mask,
    cell,
    power,
    gain
}): ReactNode => {
    const ref = useRef<HTMLPreElement>(null)

    useEffect(() => {
        const element = ref.current

        if (!element) return

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
        const maskFn = MASKS[mask] ?? MASKS.blob

        let cols = 0
        let rows = 0
        let field = new Float32Array(0)
        let time = 0
        let offsetX = 0
        let offsetY = 0
        let frame = 0
        let lastTick = 0
        let visible = false
        let disposed = false
        let resizeTimer = 0
        let observer: IntersectionObserver | null = null

        const layout = (): void => {
            const fontFamily = getComputedStyle(element).fontFamily || 'monospace'
            const { w, h } = measureCell(cell, fontFamily)
            const rect = element.getBoundingClientRect()

            cols = Math.max(1, Math.ceil(rect.width / w))
            rows = Math.max(1, Math.ceil(rect.height / h))
            field = new Float32Array(cols * rows)

            for (let row = 0; row < rows; row++) {
                for (let col = 0; col < cols; col++) field[row * cols + col] = maskFn(col / cols, row / rows)
            }
        }

        const render = (): void => {
            const step = Math.floor(time)
            const blend = smooth(time - step)
            const seedA = seed + 99 + step
            const seedB = seedA + 1
            const lines: string[] = []

            for (let row = 0; row < rows; row++) {
                let line = ''

                for (let col = 0; col < cols; col++) {
                    const index = row * cols + col

                    if (field[index] <= 0) {
                        line += ' '
                        continue
                    }

                    const base = 0.65 * noise(col / 7 + offsetX, row / 4.5 + offsetY, seed)
                    const nx = col / 2.6 + offsetX * 2.2
                    const ny = row / 2 + offsetY * 2.2
                    const detail = noise(nx, ny, seedA) + (noise(nx, ny, seedB) - noise(nx, ny, seedA)) * blend

                    line += toChar(Math.pow((base + 0.35 * detail) * field[index], power) * gain)
                }

                lines.push(line)
            }

            element.textContent = lines.join('\n')
        }

        const tick = (now: number): void => {
            if (!visible) {
                frame = 0
                return
            }

            if (now - lastTick >= FRAME_MS) {
                lastTick = now
                time += STEP_TIME
                offsetX += STEP_X
                offsetY += STEP_Y
                render()
            }

            frame = requestAnimationFrame(tick)
        }

        const start = (): void => {
            if (frame || reduced.matches) return

            lastTick = 0
            frame = requestAnimationFrame(tick)
        }

        const stop = (): void => {
            if (frame) cancelAnimationFrame(frame)
            frame = 0
        }

        const onResize = (): void => {
            window.clearTimeout(resizeTimer)
            resizeTimer = window.setTimeout(() => {
                layout()
                render()
            }, 200)
        }

        const init = (): void => {
            if (disposed) return

            layout()
            render()

            if (!reduced.matches) {
                observer = new IntersectionObserver((entries) => {
                    for (const entry of entries) {
                        visible = entry.isIntersecting

                        if (visible) start()
                        else stop()
                    }
                })
                observer.observe(element)
            }

            window.addEventListener('resize', onResize)
        }

        const ready = document.fonts?.ready ?? Promise.resolve()

        ready.then(init).catch(init)

        return () => {
            disposed = true
            observer?.disconnect()
            stop()
            window.clearTimeout(resizeTimer)
            window.removeEventListener('resize', onResize)
        }
    }, [seed, mask, cell, power, gain])

    return (
        <pre
            ref={ref}
            className={className}
            data-ascii=''
            data-ascii-seed={seed}
            data-ascii-mask={mask}
            data-ascii-cell={cell}
            data-ascii-power={power}
            data-ascii-gain={gain}
            aria-hidden='true'
        />
    )
}
